import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
// Reuse select styles for dropdown
import styles from '../select/select.module.scss';
import { createPortal } from 'react-dom';

export interface MultiselectProps<T, K = T> {
  items?: readonly T[];
  value?: K[];
  onChange?: (value: K[]) => void;
  onBlur?: () => void;
  identityFn?: (item: T) => K;
  textFn?: (item: T) => string;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  size?: 'small' | 'normal';
  hasError?: boolean;
  hasWarning?: boolean;
  className?: string;
  renderItem?: (item: T) => React.ReactNode;
}

export function Multiselect<T, K = T>({
  items = [],
  value = [],
  onChange,
  onBlur,
  identityFn = (item) => item as unknown as K,
  textFn = (item) => String(item),
  placeholder = 'Select...',
  loading,
  disabled,
  readonly,
  size = 'normal',
  hasError,
  hasWarning,
  className,
  renderItem
}: MultiselectProps<T, K>) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const { x, y, refs, strategy } = useFloating({
    placement: 'bottom-start',
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });

  const selectedItems = items.filter((item) => value.includes(identityFn(item)));
  const popperDisabled = Boolean(readonly || disabled || loading || !items.length);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (refs.domReference.current && !refs.domReference.current.contains(e.target as Node) &&
          refs.floating.current && !refs.floating.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
        onBlur?.();
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, refs, onBlur]);

  const handleOpen = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!popperDisabled) {
      e.preventDefault();
      setOpen(!open);
      setFocused(true);
    }
  };

  const handleToggle = (val: K) => {
    if (value.includes(val)) {
      onChange?.(value.filter(v => v !== val));
    } else {
      onChange?.([...value, val]);
    }
    (refs.domReference.current as HTMLElement)?.focus();
  };

  return (
    <>
      <div
        ref={refs.setReference}
        tabIndex={disabled ? undefined : 0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleOpen(e);
          if (e.key === 'ArrowDown' && !open) handleOpen(e);
        }}
        className={classNames('input select', styles.selectInput, className, {
          [styles.isReadonly]: readonly,
          'is-focused': focused,
          'is-loading': loading,
          'is-danger': hasError,
          'is-warning': hasWarning,
          'is-small': size === 'small',
        })}
      >
        {selectedItems.length > 0 ? selectedItems.map(textFn).join(', ') : placeholder}
      </div>

      {open && !popperDisabled && createPortal(
        <ul
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: refs.domReference.current?.getBoundingClientRect().width,
          }}
          className={styles.overlay}
        >
          {items.map((item, i) => {
            const itemValue = identityFn(item);
            const isSelected = value.includes(itemValue);
            return (
              <li
                key={i}
                className={classNames(styles.option, {
                  [styles.isSelected]: isSelected
                })}
                onClick={() => handleToggle(itemValue)}
              >
                <input 
                  type="checkbox" 
                  checked={isSelected} 
                  readOnly 
                  style={{ marginRight: 8 }} 
                />
                {renderItem ? renderItem(item) : textFn(item)}
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </>
  );
}
